import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../../utils/config';
import Button from '../ui/Button';
import Input from '../ui/Input';

const StoreForm = ({ store = null, onSubmit, isLoading = false }) => {
  const navigate = useNavigate();
  
  // Available currencies with symbols
  const currencies = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'INR': '₹',
    'BRL': 'R$',
    'RUB': '₽',
    'KRW': '₩',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'CHF',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RON': 'lei',
    'BGN': 'лв',
    'HRK': 'kn',
    'RSD': 'дин',
    'ALL': 'L',
    'BAM': 'KM',
    'MKD': 'ден',
    'MDL': 'L',
    'UAH': '₴',
    'BYN': 'Br',
    'AMD': '֏',
    'GEL': '₾',
    'AZN': '₼',
    'KZT': '₸',
    'KGS': 'с',
    'TJS': 'ЅМ',
    'TMT': 'T',
    'UZS': 'so\'m',
    'LAK': '₭',
    'KHR': '៛',
    'THB': '฿',
    'VND': '₫',
    'IDR': 'Rp',
    'MYR': 'RM',
    'SGD': 'S$',
    'BDT': '৳',
    'LKR': 'Rs',
    'NPR': '₨',
    'MMK': 'K',
    'KES': 'KSh',
    'TZS': 'TSh',
    'UGX': 'USh',
    'NGN': '₦',
    'GHS': '₵',
    'ZAR': 'R',
    'NAD': 'N$',
    'BWP': 'P',
    'ZMW': 'ZK',
    'MWK': 'MK',
    'ZWL': 'Z$',
    'ETB': 'Br',
    'SDG': 'ج.س',
    'EGP': 'E£',
    'LYD': 'ل.د',
    'TND': 'د.ت',
    'DZD': 'د.ج',
    'MAD': 'د.م',
    'MRU': 'UM',
    'XOF': 'CFA',
    'XAF': 'FCFA',
    'XPF': 'CFP',
    'STN': 'Db',
    'CVE': '$',
    'GMD': 'D',
    'LRD': 'L$',
    'SLL': 'Le',
    'GNF': 'FG',
    'IRR': '﷼',
    'IQD': 'ع.د',
    'JOD': 'د.ا',
    'LBP': 'ل.ل',
    'SYP': 'ل.س',
    'YER': '﷼',
    'OMR': 'ر.ع',
    'QAR': 'ر.ق',
    'SAR': 'ر.س',
    'AED': 'د.إ',
    'BHD': 'د.ب',
    'KWD': 'د.ك',
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    contact_email: '',
    location: '',
    redirect_url: '',
    currency: 'USD',
    icon: null,
    remove_icon: false
  });
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (store) {
      setFormData({
        name: store.name || '',
        email: store.email || '',
        phone: store.phone || '',
        contact_email: store.contact_email || '',
        location: store.location || '',
        redirect_url: store.redirect_url || '',
        currency: store.currency || 'USD',
        icon: null,
        remove_icon: false
      });
      if (store.icon) {
        setPreview(getImageUrl(store.icon));
      }
    }
  }, [store]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        icon: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Store name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid contact email';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (formData.redirect_url && !/^https?:\/\/.+/.test(formData.redirect_url)) {
      newErrors.redirect_url = 'Please enter a valid URL starting with http:// or https://';
    }
    

    
    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      // Handle Laravel validation errors
      if (error.response?.data?.errors) {
        const validationErrors = {};
        Object.keys(error.response.data.errors).forEach(key => {
          validationErrors[key] = error.response.data.errors[key][0];
        });
        setErrors(validationErrors);
      } else if (error.response?.data?.message) {
        // Handle other API errors
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'An unexpected error occurred' });
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {store ? 'Edit Store' : 'Create New Store'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          {/* Store Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Store Name *
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              error={errors.name}
              placeholder="Enter store name"
            />
          </div>

          {/* Email (only for create) */}
          {!store && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleInputChange}
                error={errors.email}
                placeholder="store@example.com"
              />
            </div>
          )}

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone *
            </label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              error={errors.phone}
              placeholder="+1234567890"
            />
          </div>

          {/* Contact Email */}
          <div>
            <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email *
            </label>
            <Input
              id="contact_email"
              name="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={handleInputChange}
              error={errors.contact_email}
              placeholder="contact@example.com"
            />
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
              Location *
            </label>
            <Input
              id="location"
              name="location"
              type="text"
              value={formData.location}
              onChange={handleInputChange}
              error={errors.location}
              placeholder="Store address"
            />
          </div>

          {/* Redirect URL */}
          <div>
            <label htmlFor="redirect_url" className="block text-sm font-medium text-gray-700 mb-2">
              Redirect URL
            </label>
            <Input
              id="redirect_url"
              name="redirect_url"
              type="url"
              value={formData.redirect_url}
              onChange={handleInputChange}
              error={errors.redirect_url}
              placeholder="https://example.com"
            />
          </div>



          {/* Currency */}
          <div>
            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
              Currency *
            </label>
            <select
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.currency ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {Object.entries(currencies).map(([code, symbol]) => (
                <option key={code} value={code}>
                  {symbol} - {code}
                </option>
              ))}
            </select>
            {errors.currency && (
              <p className="mt-1 text-sm text-red-600">{errors.currency}</p>
            )}
          </div>

          {/* Icon Upload (always show) */}
          <div>
            <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-2">
              Store Icon
            </label>
            <div className="space-y-4">
              <div className="flex items-center justify-center w-full">
                <label htmlFor="icon" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                  <input
                    id="icon"
                    name="icon"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
              {/* Preview */}
              {preview && (
                <div className="flex items-center space-x-4">
                  <img
                    src={preview}
                    alt="Store icon preview"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreview(null);
                      setFormData(prev => ({ ...prev, icon: null, remove_icon: true }));
                    }}
                    className="text-sm text-red-600 hover:text-red-800 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : (store ? 'Update Store' : 'Create Store')}
            </Button>
            
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/admin/stores')}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoreForm; 